import { useCallback } from "react";
import { useCancelCloseMutation } from "@api/close.api";
import {
    type WorkItemEdit,
    getWorkItemErrorMessage,
    useUpdateWorkItem,
} from "@api/workItem.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { parseCloseId } from "@hooks/useOpenWorkRow";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";
import {
    type WorkSkipReason,
    getWorkSkipReason,
} from "../editing/workItemEditing";
import {
    type WorkBulkAction,
    summarizeWorkBulkResult,
} from "./workBulkSummary";

const CANCEL_WORK_ITEM_FAILED = "Failed to cancel work item";

/**
 * Runs an edit or Cancel on every open item at once (done and canceled ones
 * are skipped, and for a role edit so are items with no open tasks for that
 * role) and toasts the outcome once. A single item that fails shows the
 * reason the backend gave.
 */
export const useWorkBulkEdits = () => {
    const { mutateAsync: updateWorkItem } = useUpdateWorkItem();
    const { mutateAsync: cancelClose } = useCancelCloseMutation({
        shouldSuppressErrorToast: true,
    });
    const { toast, error } = useToaster();

    const run = useCallback(
        async ({
            rows,
            action,
            edit,
            apply,
        }: {
            rows: ClientsWorkOverviewWorkRow[];
            action: WorkBulkAction;
            /** The edit to apply; unset for Cancel. */
            edit?: WorkItemEdit;
            apply: (row: ClientsWorkOverviewWorkRow) => Promise<void>;
        }) => {
            const skippedCounts: Record<WorkSkipReason, number> = {
                doneOrCanceled: 0,
                noOpenTasks: 0,
            };
            const appliedRows = rows.filter((row) => {
                const skipReason = getWorkSkipReason(row, edit);
                if (skipReason) {
                    skippedCounts[skipReason] += 1;
                }
                return skipReason === null;
            });
            const results = await Promise.allSettled(appliedRows.map(apply));
            const failures = results.filter(
                (result) => result.status === "rejected",
            );
            const { message, isError } = summarizeWorkBulkResult({
                action,
                succeededCount: results.length - failures.length,
                failedCount: failures.length,
                skippedCounts,
            });
            const [failure] = failures;
            const reason: Error | undefined =
                rows.length === 1 && failure?.reason instanceof Error
                    ? failure.reason
                    : undefined;
            if (isError) {
                error(reason ? reason.message : message);
            } else {
                toast(message);
            }
        },
        [toast, error],
    );

    const editAll = useCallback(
        (rows: ClientsWorkOverviewWorkRow[], edit: WorkItemEdit) =>
            run({
                rows,
                action: "update",
                edit,
                apply: async ({ closeId, workspaceId }) => {
                    await updateWorkItem({ closeId, workspaceId, edit });
                },
            }),
        [run, updateWorkItem],
    );

    const cancelAll = useCallback(
        (rows: ClientsWorkOverviewWorkRow[]) =>
            run({
                rows,
                action: "cancel",
                apply: async (row) => {
                    const closeId = parseCloseId(row.closeId);
                    if (!closeId) {
                        throw new Error(CANCEL_WORK_ITEM_FAILED);
                    }
                    try {
                        await cancelClose(closeId);
                    } catch (cancelError) {
                        throw new Error(
                            getWorkItemErrorMessage(
                                cancelError,
                                CANCEL_WORK_ITEM_FAILED,
                            ),
                        );
                    }
                },
            }),
        [run, cancelClose],
    );

    return { editAll, cancelAll };
};
